import { DateTime } from 'luxon';
import { policyDetailsType } from '@/types/PolicyDetailsType';

/**
 * Account/carrier/wholesaler scenarios fetch policies from `/…/{routeId}/policies`,
 * where `routeId` is read live from `$route.params.id`. If the route no longer carries
 * an id — e.g. the user navigated away while the page was still loading — that reads
 * back as `undefined` and we'd request `/…/undefined/policies`. Skip the fetch in that
 * case. The Policy Explorer (`All`) fetches by search params and `Program` renders
 * preloaded data, so neither needs a route id.
 */
export function shouldSkipPolicyFetch(scenario: policyDetailsType | string | undefined, routeId: string | undefined | null): boolean {
  if (scenario === policyDetailsType.All || scenario === policyDetailsType.Program) {
    return false;
  }
  return !routeId;
}

/**
 * Reads a by-name filter out of the query string, keeping only values the current option
 * list still offers. A stale or hand-edited link would otherwise apply a filter the user
 * cannot see in the panel and cannot clear.
 */
export function parseNamedFilter(raw: unknown, validValues: string[]): string[] {
  if (typeof raw !== 'string' || raw === '') {
    return [];
  }
  const valid = new Set(validValues);
  return raw.split(',').filter((name) => valid.has(name));
}

export interface QueryParams {
  start?: string; // start date
  end?: string; // end date
  col?: string; // visible columns in the table
  cov?: string; // coverage types
  prod?: string; // products, by name
  sic?: string; // SIC industry types
  ind?: string; // industry types
  wg?: string; // wholesaler groups
  cg?: string; // carrier groups
  at?: string; // attachment points
  r?: string; // retention
  ca?: string; // client attributes
  ag?: string; // agency types
  dept?: string; // departments
  ext?: string; // show external policies
  exp?: string; // show expired policies
  due?: string; // show policies within expiration window
  own?: string; // ownership types (comma-separated: Public,Private,Not Specified)
  run_y?: string; // runoff
  run_n?: string; // no runoff
  pri?: string; // primary
  exs?: string; // excess
  fav?: string; // favorite accounts only
  cur?: string; // current only
}

export const enum POLICY_TABLE_COLUMNS {
  YEAR = 'year',
  ACCOUNT_NAME = 'accountName',
  SOURCE_ACCOUNT_NAME = 'sourceAccountName',
  ACCOUNT_ATTRIBUTES = 'accountAttributes',
  PARENT_ACCOUNT_NAME = 'parentAccountName',
  POLICY_TYPE = 'policyType',
  COVERAGE_TYPE = 'coverageType',
  PRODUCT = 'product',
  DESCRIPTION = 'description',
  TOP_LEVEL_INDUSTRY = 'topLevelIndustry',
  GENERIC_INDUSTRY = 'industry',
  OWNERSHIP_TYPE = 'ownershipType',
  CARRIER_GROUP = 'carrierGroup',
  ISSUING_COMPANY_NAME = 'issuingCompanyName',
  CARRIER_RATING = 'carrierRating',
  SYNDICATE = 'syndicate',
  SYNDICATE_AGENT = 'syndicateAgent',
  WHOLESALER = 'wholesaler',
  UNDERWRITER_NAME = 'underwriterName',
  LIMIT = 'limit',
  OCCURRENCE_LIMIT = 'occurrenceLimit',
  ATTACHMENT_POINT = 'attachmentPoint',
  RETENTION = 'retention',
  PREMIUM = 'premium',
  PREMIUM_ANNUALIZED = 'premiumAnnualized',
  PPM = 'ppm',
  ANNUALIZED_PPM = 'annualizedPPM',
  ILF = 'ilf',
  AUDIT_AMT = 'auditAmt',
  QUOTA_SHARE_TOTAL = 'quotaShareTotal',
  POLICY_ID = 'policyID',
  AI_STATUS = 'aiStatus',
  EFFECTIVE_DATE = 'effectiveDate',
  EXPIRATION_DATE = 'expirationDate',
  SIDE_A_TYPE = 'sideAType',
  RUNOFF = 'runoff',
  LINE_STATUS_CODE = 'lineStatusCode',
  BILL_MODE = 'billMode',
  AGENCY_NAME = 'agencyName',
  COMMISSION_BILLED = 'commissionBilled',
  FEES_BILLED = 'feesBilled',
  ENTERED_DATE = 'enteredDate',
  PREMIUM_PAYABLE_NAME = 'premiumPayableName',
  REBATE_AMT = 'rebateAmt',
  BROKER = 'broker',
  PROFIT_CENTER = 'profitCenter',
  CURR_TERM_AMOUNT = 'currTermAmount',
  OBLIGEE_NAME = 'obligeeName',
  OBLIGEE_ADDRESS = 'obligeeAddress',
  PRINCIPAL_NAME = 'principalName',
  PRINCIPAL_ADDRESS = 'principalAddress',
  UNDERWRITING_NAME = 'underwritingName',
  UNDERWRITING_ADDRESS = 'underwritingAddress',
  CANCELABLE = 'cancelable',
  NET_AMOUNT = 'netAmount',
}

/**
 * The consolidated (program details) table renders 8 columns instead of 13 by merging
 * related fields into a single cell — see CAC-5074. Columns that map 1:1 onto a policy
 * field keep that field's key; only the merged ones (`policy`, `term`,
 * `retentionAttachment`, `limits`) are synthetic. `sourceFields` is what each column
 * expands back into for CSV export, so a download still emits atomic spreadsheet columns.
 */
export interface ConsolidatedColumn {
  value: string;
  text: string;
  stack: 'Policy';
  sourceFields: string[];
}

/** `POLICY_TABLE_COLUMNS.LIMIT` is the sort key (`limit`); the policy field itself is `aggregateLimit`. */
const AGGREGATE_LIMIT_FIELD = 'aggregateLimit';

export const CONSOLIDATED_POLICY_COLUMNS: ConsolidatedColumn[] = [
  { value: 'policy', text: 'Policy', stack: 'Policy', sourceFields: [POLICY_TABLE_COLUMNS.DESCRIPTION, POLICY_TABLE_COLUMNS.POLICY_ID] },
  { value: POLICY_TABLE_COLUMNS.AI_STATUS, text: 'AI', stack: 'Policy', sourceFields: [POLICY_TABLE_COLUMNS.AI_STATUS] },
  { value: 'term', text: 'Term', stack: 'Policy', sourceFields: [POLICY_TABLE_COLUMNS.EFFECTIVE_DATE, POLICY_TABLE_COLUMNS.EXPIRATION_DATE] },
  { value: POLICY_TABLE_COLUMNS.CARRIER_GROUP, text: 'Carrier', stack: 'Policy', sourceFields: [POLICY_TABLE_COLUMNS.CARRIER_GROUP] },
  {
    value: 'retentionAttachment',
    text: 'Retention / Attachment',
    stack: 'Policy',
    sourceFields: [POLICY_TABLE_COLUMNS.RETENTION, POLICY_TABLE_COLUMNS.ATTACHMENT_POINT],
  },
  {
    value: 'limits',
    text: 'Limits',
    stack: 'Policy',
    sourceFields: [POLICY_TABLE_COLUMNS.OCCURRENCE_LIMIT, AGGREGATE_LIMIT_FIELD],
  },
  { value: POLICY_TABLE_COLUMNS.PREMIUM, text: 'Premium', stack: 'Policy', sourceFields: [POLICY_TABLE_COLUMNS.PREMIUM] },
];

/**
 * Context columns the "Select Policies" table (create / edit / merge a program) prepends
 * to the consolidated layout. Picking policies means choosing across coverage lines, so
 * Coverage Type stays; Account Name only earns its width on a parent account, which we
 * read off the policies themselves rather than a separate account lookup.
 */
export const CONSOLIDATED_CONTEXT_COLUMNS: ConsolidatedColumn[] = [
  {
    value: POLICY_TABLE_COLUMNS.COVERAGE_TYPE,
    text: 'Coverage Type',
    stack: 'Policy',
    sourceFields: [POLICY_TABLE_COLUMNS.COVERAGE_TYPE],
  },
  {
    value: POLICY_TABLE_COLUMNS.ACCOUNT_NAME,
    text: 'Account Name',
    stack: 'Policy',
    sourceFields: [POLICY_TABLE_COLUMNS.ACCOUNT_NAME],
  },
];

/** Context columns for `policies`: Coverage Type always, Account Name once they span more than one account. */
export function consolidatedContextColumns(policies: { accountName?: string }[] | undefined): ConsolidatedColumn[] {
  const accountNames = new Set((policies ?? []).map((p) => p.accountName).filter(Boolean));
  return CONSOLIDATED_CONTEXT_COLUMNS.filter((c) => c.value !== POLICY_TABLE_COLUMNS.ACCOUNT_NAME || accountNames.size > 1);
}

/**
 * Header labels for the consolidated policy table, for views that render a loading skeleton
 * before PolicyDetails mounts and so cannot read its `fields` computed.
 */
export function consolidatedColumnLabels(canSeeAIStatus: boolean, leadingColumns: string[] = []): string[] {
  return [...leadingColumns, ...CONSOLIDATED_POLICY_COLUMNS.filter((c) => canSeeAIStatus || c.value !== 'aiStatus').map((c) => c.text)];
}

/** Expands consolidated column ids back into the underlying policy fields (for CSV export). */
export function expandConsolidatedColumns(fields: string[]): string[] {
  const bySource = new Map(CONSOLIDATED_POLICY_COLUMNS.map((c) => [c.value, c.sourceFields]));
  const expanded = fields.flatMap((f) => bySource.get(f) ?? [f]);
  return Array.from(new Set(expanded));
}

export interface PolicySort {
  key: string;
  direction: 'ascending' | 'descending';
}

export interface PolicySortOption {
  value: string;
  label: string;
  /** Shown while the option runs in its default direction. */
  description: string;
  /** Shown once the option has been reversed; states the opposite order in full. */
  reversedDescription: string;
  sorts: PolicySort[];
}

/**
 * Curated sort options for the consolidated table. Replaces click-any-header sorting
 * with a small explicit menu (CAC-5074). `Layer` relies on primary policies having no
 * attachment point, so ascending puts them above the excess layers.
 *
 * Picking the already-active option reverses it — see `reverseSorts`.
 */
export const CONSOLIDATED_SORT_OPTIONS: PolicySortOption[] = [
  {
    value: 'layer',
    label: 'Layer',
    description: 'Primary first, then by attachment',
    reversedDescription: 'Highest attachment first',
    sorts: [
      { key: POLICY_TABLE_COLUMNS.ATTACHMENT_POINT, direction: 'ascending' },
      { key: POLICY_TABLE_COLUMNS.POLICY_ID, direction: 'ascending' },
    ],
  },
  {
    value: 'retention',
    label: 'Retention',
    description: 'Highest retention first',
    reversedDescription: 'Lowest retention first',
    sorts: [
      { key: POLICY_TABLE_COLUMNS.RETENTION, direction: 'descending' },
      { key: POLICY_TABLE_COLUMNS.POLICY_ID, direction: 'ascending' },
    ],
  },
  {
    value: 'attachment',
    label: 'Attachment',
    description: 'Highest attachment first',
    reversedDescription: 'Lowest attachment first',
    sorts: [
      { key: POLICY_TABLE_COLUMNS.ATTACHMENT_POINT, direction: 'descending' },
      { key: POLICY_TABLE_COLUMNS.POLICY_ID, direction: 'ascending' },
    ],
  },
  {
    value: 'premium',
    label: 'Premium',
    description: 'Highest premium first',
    reversedDescription: 'Lowest premium first',
    sorts: [{ key: POLICY_TABLE_COLUMNS.PREMIUM, direction: 'descending' }],
  },
  {
    value: 'carrier',
    label: 'Carrier',
    description: 'A → Z',
    reversedDescription: 'Z → A',
    sorts: [{ key: POLICY_TABLE_COLUMNS.CARRIER_GROUP, direction: 'ascending' }],
  },
  {
    value: 'effective',
    label: 'Effective Date',
    description: 'Newest effective date first',
    reversedDescription: 'Oldest effective date first',
    sorts: [
      { key: POLICY_TABLE_COLUMNS.EFFECTIVE_DATE, direction: 'descending' },
      { key: POLICY_TABLE_COLUMNS.POLICY_ID, direction: 'ascending' },
    ],
  },
  {
    value: 'expiration',
    label: 'Expiration Date',
    description: 'Newest expiration date first',
    reversedDescription: 'Oldest expiration date first',
    sorts: [
      { key: POLICY_TABLE_COLUMNS.EXPIRATION_DATE, direction: 'descending' },
      { key: POLICY_TABLE_COLUMNS.POLICY_ID, direction: 'ascending' },
    ],
  },
  {
    value: 'occurrence',
    label: 'Occurrence Limit',
    description: 'Highest occurrence limit first',
    reversedDescription: 'Lowest occurrence limit first',
    sorts: [
      { key: POLICY_TABLE_COLUMNS.OCCURRENCE_LIMIT, direction: 'descending' },
      { key: POLICY_TABLE_COLUMNS.POLICY_ID, direction: 'ascending' },
    ],
  },
  {
    value: 'aggregate',
    label: 'Aggregate Limit',
    description: 'Highest aggregate limit first',
    reversedDescription: 'Lowest aggregate limit first',
    sorts: [
      { key: AGGREGATE_LIMIT_FIELD, direction: 'descending' },
      { key: POLICY_TABLE_COLUMNS.POLICY_ID, direction: 'ascending' },
    ],
  },
];

export const DEFAULT_CONSOLIDATED_SORT = CONSOLIDATED_SORT_OPTIONS[0];

const HEADER_SORT_OPTIONS: Record<string, string> = {
  carrierGroup: 'carrier',
  premium: 'premium',
};

/**
 * Merged columns cover two sort options each. The header drives whichever of the two is
 * already active so a second click reverses it, and falls back to the first-listed
 * option when neither is.
 */
const MERGED_HEADER_SORT_OPTIONS: Record<string, [string, string]> = {
  retentionAttachment: ['retention', 'attachment'],
  term: ['effective', 'expiration'],
  limits: ['occurrence', 'aggregate'],
};

/**
 * The sort option a consolidated column header drives when clicked, or undefined for
 * columns that have no matching option.
 */
export function sortOptionForColumn(column: string, activeValue?: string): string | undefined {
  const merged = MERGED_HEADER_SORT_OPTIONS[column];
  if (merged) {
    return activeValue && merged.includes(activeValue) ? activeValue : merged[0];
  }
  return HEADER_SORT_OPTIONS[column];
}

/**
 * Flips the primary sort key only. Trailing keys stay as tie-breakers, so rows that tie
 * on the primary key keep the same predictable order in both directions — reversing the
 * tie-breaker too would shuffle equal rows for no reason the user can see.
 */
export function reverseSorts(sorts: PolicySort[]): PolicySort[] {
  return sorts.map((sort, index) => (index === 0 ? { ...sort, direction: sort.direction === 'ascending' ? 'descending' : 'ascending' } : sort));
}

/** Resolves the option plus its reversed flag into the sorts, description and arrow to render. */
export function resolveConsolidatedSort(
  option: PolicySortOption,
  reversed: boolean,
): { sorts: PolicySort[]; description: string; direction: 'ascending' | 'descending' } {
  const sorts = reversed ? reverseSorts(option.sorts) : option.sorts;
  return {
    sorts,
    description: reversed ? option.reversedDescription : option.description,
    direction: sorts[0].direction,
  };
}

const NUMERIC_SORT_FIELDS = new Set<string>([
  POLICY_TABLE_COLUMNS.ATTACHMENT_POINT,
  POLICY_TABLE_COLUMNS.RETENTION,
  POLICY_TABLE_COLUMNS.OCCURRENCE_LIMIT,
  AGGREGATE_LIMIT_FIELD,
  POLICY_TABLE_COLUMNS.PREMIUM,
  POLICY_TABLE_COLUMNS.PREMIUM_ANNUALIZED,
]);

const DATE_SORT_FIELDS = new Set<string>([POLICY_TABLE_COLUMNS.EFFECTIVE_DATE, POLICY_TABLE_COLUMNS.EXPIRATION_DATE]);

function toMillis(value: unknown): number {
  if (!value) return 0;
  const millis = DateTime.fromISO(String(value), { zone: 'utc' }).toMillis();
  return Number.isNaN(millis) ? 0 : millis;
}

function compareField(field: string, a: Record<string, unknown>, b: Record<string, unknown>): number {
  if (NUMERIC_SORT_FIELDS.has(field)) {
    return (Number(a[field]) || 0) - (Number(b[field]) || 0);
  }
  if (DATE_SORT_FIELDS.has(field)) {
    return toMillis(a[field]) - toMillis(b[field]);
  }
  return String(a[field] ?? '').localeCompare(String(b[field] ?? ''));
}

/**
 * Sorts policies in place of the DOM-mutating `v-tablesort` directive. Keeping the sort
 * in the data layer means Vue owns the row order, so the table can be re-rendered (expand,
 * refresh, filter) without the vdom drifting out of sync with the DOM.
 */
export function sortPolicies<T extends Record<string, unknown>>(policies: T[], sorts: PolicySort[] | undefined): T[] {
  if (!sorts || sorts.length === 0) return policies;
  return policies.slice().sort((a, b) => {
    for (const { key, direction } of sorts) {
      const result = compareField(key, a, b);
      if (result !== 0) return direction === 'descending' ? -result : result;
    }
    return 0;
  });
}

export const USStates = [
  { label: 'Alabama', value: 'AL' },
  { label: 'Alaska', value: 'AK' },
  { label: 'Arizona', value: 'AZ' },
  { label: 'Arkansas', value: 'AR' },
  { label: 'California', value: 'CA' },
  { label: 'Colorado', value: 'CO' },
  { label: 'Connecticut', value: 'CT' },
  { label: 'Delaware', value: 'DE' },
  { label: 'Florida', value: 'FL' },
  { label: 'Georgia', value: 'GA' },
  { label: 'Hawaii', value: 'HI' },
  { label: 'Idaho', value: 'ID' },
  { label: 'Illinois', value: 'IL' },
  { label: 'Indiana', value: 'IN' },
  { label: 'Iowa', value: 'IA' },
  { label: 'Kansas', value: 'KS' },
  { label: 'Kentucky', value: 'KY' },
  { label: 'Louisiana', value: 'LA' },
  { label: 'Maine', value: 'ME' },
  { label: 'Maryland', value: 'MD' },
  { label: 'Massachusetts', value: 'MA' },
  { label: 'Michigan', value: 'MI' },
  { label: 'Minnesota', value: 'MN' },
  { label: 'Mississippi', value: 'MS' },
  { label: 'Missouri', value: 'MO' },
  { label: 'Montana', value: 'MT' },
  { label: 'Nebraska', value: 'NE' },
  { label: 'Nevada', value: 'NV' },
  { label: 'New Hampshire', value: 'NH' },
  { label: 'New Jersey', value: 'NJ' },
  { label: 'New Mexico', value: 'NM' },
  { label: 'New York', value: 'NY' },
  { label: 'North Carolina', value: 'NC' },
  { label: 'North Dakota', value: 'ND' },
  { label: 'Ohio', value: 'OH' },
  { label: 'Oklahoma', value: 'OK' },
  { label: 'Oregon', value: 'OR' },
  { label: 'Pennsylvania', value: 'PA' },
  { label: 'Rhode Island', value: 'RI' },
  { label: 'South Carolina', value: 'SC' },
  { label: 'South Dakota', value: 'SD' },
  { label: 'Tennessee', value: 'TN' },
  { label: 'Texas', value: 'TX' },
  { label: 'Utah', value: 'UT' },
  { label: 'Vermont', value: 'VT' },
  { label: 'Virginia', value: 'VA' },
  { label: 'Washington', value: 'WA' },
  { label: 'West Virginia', value: 'WV' },
  { label: 'Wisconsin', value: 'WI' },
  { label: 'Wyoming', value: 'WY' },
  { label: 'D.C.', value: 'DC' },
];

export const USStatesMap = new Map<string, string>(USStates.map((state) => [state.value, state.label]));
